// We need to be able to create a simulation with a set address and situation.
// We must be able to start, pause, and stop the simulation.

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./ui/Button";
import Title from "./ui/Title";
import Modal from "./ui/Modal";
import { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import BACKEND_URL from "../config";
import { Situation, Simulation, Prompt, Phase, Role, PagerSnapshot } from "../types";
import { toast } from "react-toastify";
import { IconEye, IconPlayerPlayFilled, IconPlayerPauseFilled, IconPlayerStopFilled, IconPlayerTrackNextFilled } from "@tabler/icons-react";
import PDOKAddressSearch from "./PDOKAddressSearch";
import TagMultiSelect from "./ui/TagMultiSelect";
import Pager from "./Pager";

const TransportButton = ({ label, onClick, disabled, children, active }: { label: string, onClick: () => void, disabled?: boolean, children: React.ReactNode, active?: boolean }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        title={label}
        className={`
            flex flex-col items-center justify-center
            h-14 rounded-md border
            disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors
            ${active
                ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'}
        `}
    >
        {children}
    </button>
);

type MenuPromptType = 'characteristics_estimation' | 'ai_operator' | 'ai_summary';

const SelectorTile = ({ value, emptyCta, onClick, subLabel }: { value: string | null, emptyCta: string, onClick: () => void, subLabel?: string }) => {
    const filled = value !== null && value !== '';
    if (!filled) {
        return (
            <button
                type="button"
                onClick={onClick}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
                <span className="text-lg leading-none">+</span>
                <span className="text-sm font-medium">{emptyCta}</span>
            </button>
        );
    }
    return (
        <div className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-col min-w-0">
                {subLabel && <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{subLabel}</span>}
                <span className="text-sm font-medium text-gray-900 truncate">{value}</span>
            </div>
            <button
                type="button"
                onClick={onClick}
                className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
            >
                Wijzig
            </button>
        </div>
    );
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{children}</h3>
);


const PromptSelector = ({ prompts, selectedPrompt, setPrompt, promptType, onPromptCreated }: { prompts: Prompt[], selectedPrompt: Prompt | undefined, setPrompt: (prompt: Prompt | undefined) => void, promptType: string, onPromptCreated?: (prompt: Prompt) => void }) => {
    const [viewingPrompt, setViewingPrompt] = useState<Prompt | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newPromptName, setNewPromptName] = useState('');
    const [newPromptContent, setNewPromptContent] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const handleCreatePrompt = async () => {
        if (!newPromptName.trim()) return;
        setIsCreating(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/prompts/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: JSON.stringify({
                    name: newPromptName,
                    prompt_type: promptType,
                    prompt: newPromptContent,
                }),
            });
            const data = await response.json();
            if (response.ok) {
                setNewPromptName('');
                setNewPromptContent('');
                setIsCreateModalOpen(false);
                if (onPromptCreated) {
                    onPromptCreated(data);
                }
            } else {
                toast.error(data.error || 'Failed to create prompt');
            }
        } catch (error) {
            toast.error('Failed to create prompt');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex gap-3" style={{ height: '200px' }}>
                <div className="flex-1 overflow-y-auto pr-2" style={{ flex: '2' }}>
                    <div className="flex flex-wrap gap-3">
                        {prompts.map(prompt => {
                            const isSelected = selectedPrompt?.id === prompt.id;
                            return (
                                <div
                                    key={prompt.id}
                                    onClick={() => setPrompt(prompt)}
                                    className={`
                                        relative bg-white border rounded-lg p-4 cursor-pointer transition-all
                                        ${isSelected 
                                            ? 'border-blue-500 ring-2 ring-blue-200 shadow-md' 
                                            : 'border-gray-300 hover:border-gray-400 hover:shadow-lg'
                                        }
                                    `}
                                    style={{ minWidth: '200px', maxWidth: '300px', flex: '1 1 auto' }}
                                >
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <h3 className="font-medium text-gray-900 text-sm">{prompt.name}</h3>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setViewingPrompt(prompt);
                                            }}
                                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                            aria-label="View full prompt"
                                        >
                                            <IconEye size={18} />
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-600 line-clamp-3 break-words">
                                        {prompt.prompt}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div style={{ flex: '1' }} className="flex items-center justify-center">
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="w-full h-full bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-50 flex flex-col items-center justify-center gap-2"
                    >
                        <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">Create New</span>
                    </button>
                </div>
            </div>
            
            {viewingPrompt && (
                <Modal isOpen={!!viewingPrompt} onRequestClose={() => setViewingPrompt(null)}>
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-3xl w-[90vw] max-h-[80vh] overflow-auto">
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">{viewingPrompt.name}</h2>
                            <button
                                onClick={() => setViewingPrompt(null)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                aria-label="Close"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <span className="text-sm font-medium text-gray-700">Type: </span>
                                <span className="text-sm text-gray-600">{viewingPrompt.prompt_type}</span>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-700">Prompt:</span>
                                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                                        {viewingPrompt.prompt}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {isCreateModalOpen && (
                <Modal isOpen={isCreateModalOpen} onRequestClose={() => setIsCreateModalOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-[90vw] max-h-[80vh] overflow-auto">
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">Create New Prompt</h2>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                aria-label="Close"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Type</label>
                                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                                    {promptType}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={newPromptName}
                                    onChange={(e) => setNewPromptName(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter prompt name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Content</label>
                                <textarea
                                    value={newPromptContent}
                                    onChange={(e) => setNewPromptContent(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter prompt content"
                                    rows={10}
                                />
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreatePrompt}
                                    disabled={!newPromptName.trim() || isCreating}
                                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isCreating ? 'Creating...' : 'Create Prompt'}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

interface SimulationManagerProps {
    setSimulationLoading: (loading: boolean) => void;
    setSimulationToken: (token: string) => void;
    resetAll: () => void;
    selectedToken?: string | null;
    onSimulationDetailLoaded?: (detail: Record<string, unknown>) => void;
    pagerSnapshot?: PagerSnapshot | null;
    simulationElapsed?: string;
}

const SimulationManager = ({ setSimulationLoading, setSimulationToken, resetAll, selectedToken, onSimulationDetailLoaded, pagerSnapshot, simulationElapsed }: SimulationManagerProps) => {
    const navigate = useNavigate();
    const [zipcode, setZipcode] = useState('');
    const [huisnummer, setHuisnummer] = useState('');
    const [situation, setSituation] = useState<Situation | null>(null);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [characteristicsEstimationPrompt, setCharacteristicsEstimationPrompt] = useState<Prompt | undefined>(undefined);
    const [aiOperatorPrompt, setAiOperatorPrompt] = useState<Prompt | undefined>(undefined);
    const [aiSummaryPrompt, setAiSummaryPrompt] = useState<Prompt | undefined>(undefined);
    const [aiFinalRecommendationFilterPrompt, setAiFinalRecommendationFilterPrompt] = useState<Prompt | undefined>(undefined);
    const [activeSimulation, setActiveSimulation] = useState<Simulation | null>(null);
    const [previousActiveSimulation, setPreviousActiveSimulation] = useState<Simulation | null>(null);
    
    const [simulations, setSimulations] = useState<Simulation[]>([]);
    const [simulationsPage, setSimulationsPage] = useState<number>(1);
    const [simulationsPageSize, setSimulationsPageSize] = useState<number>(10);
    const [simulationsTotal, setSimulationsTotal] = useState<number>(0);
    const [simulationsLoading, setSimulationsLoading] = useState(false);


    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeSituationName, setActiveSituationName] = useState<string | null>(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    
    // Modal states for creating simulations
    const [modalZipcode, setModalZipcode] = useState('');
    const [modalHouseNumber, setModalHouseNumber] = useState('');
    const [modalSelectedSituationId, setModalSelectedSituationId] = useState<string>('');
    const [modalCreating, setModalCreating] = useState(false);
    const [modalSelectedMode, setModalSelectedMode] = useState<'free' | 'guided' | null>(null);
    const [modalSituations, setModalSituations] = useState<{id: number, name: string, gms_id?: string | null}[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [menuTab, setMenuTab] = useState<'simulaties' | 'prompts'>('simulaties');
    const [menuPromptSubTab, setMenuPromptSubTab] = useState<MenuPromptType>('characteristics_estimation');
    const [menuSearchQuery, setMenuSearchQuery] = useState('');
    const [isAddressTileModalOpen, setIsAddressTileModalOpen] = useState(false);
    const [isScenarioPickerOpen, setIsScenarioPickerOpen] = useState(false);
    const [tileZipcode, setTileZipcode] = useState('');
    const [tileHouseNumber, setTileHouseNumber] = useState('');
    const [previewPrompt, setPreviewPrompt] = useState<Prompt | null>(null);
    const [isAddPromptModalOpen, setIsAddPromptModalOpen] = useState(false);
    const [newPromptName, setNewPromptName] = useState('');
    const [newPromptContent, setNewPromptContent] = useState('');
    const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);
    const [isAdminUser, setIsAdminUser] = useState(false);

    // Phase / Role / Relevance state for the create simulation modal.
    // Default is "all selected" (matches backend semantics where empty = all).
    const [availablePhases, setAvailablePhases] = useState<Phase[]>([]);
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
    const [modalSelectedPhaseIds, setModalSelectedPhaseIds] = useState<number[]>([]);
    const [modalSelectedRoleIds, setModalSelectedRoleIds] = useState<number[]>([]);
    const [modalRelevanceThreshold, setModalRelevanceThreshold] = useState<number>(1);

    const hasAddress = zipcode.trim().length > 0 && huisnummer.trim().length > 0;

    const canCreate = useMemo(() => zipcode.trim() && huisnummer.trim() && situation, [zipcode, huisnummer, situation]);

    const fetchPrompts = () => {
        fetch(`${BACKEND_URL}/vnog/get_all_prompts/`, { credentials: 'include' })
            .then(r => r.json())
            .then(data => {
                setPrompts(data);

                const characteristicsPrompts = data.filter((p: Prompt) => p.prompt_type === 'characteristics_estimation');
                const aiOperatorPrompts = data.filter((p: Prompt) => p.prompt_type === 'ai_operator');
                const aiSummaryPrompts = data.filter((p: Prompt) => p.prompt_type === 'ai_summary');
                const aiFinalFilterPrompts = data.filter((p: Prompt) => p.prompt_type === 'ai_final_recommendation_filter');

                setCharacteristicsEstimationPrompt(prev => prev ?? characteristicsPrompts[0]);
                setAiOperatorPrompt(prev => prev ?? aiOperatorPrompts[0]);
                setAiSummaryPrompt(prev => prev ?? aiSummaryPrompts[0]);
                setAiFinalRecommendationFilterPrompt(prev => prev ?? aiFinalFilterPrompts[0]);
            })
            .catch(() => {});
    };

    const handlePromptCreated = (promptType: string, newPrompt: Prompt) => {
        // Refresh prompts list
        fetchPrompts();
        // Select the newly created prompt
        if (promptType === 'characteristics_estimation') {
            setCharacteristicsEstimationPrompt(newPrompt);
        } else if (promptType === 'ai_operator') {
            setAiOperatorPrompt(newPrompt);
        } else if (promptType === 'ai_summary') {
            setAiSummaryPrompt(newPrompt);
        } else if (promptType === 'ai_final_recommendation_filter') {
            setAiFinalRecommendationFilterPrompt(newPrompt);
        }
        toast.success('Prompt created successfully');
    };

    const createNewPrompt = async () => {
        if (!newPromptName.trim()) return;
        setIsCreatingPrompt(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/prompts/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: JSON.stringify({
                    name: newPromptName,
                    prompt_type: menuPromptSubTab,
                    prompt: newPromptContent,
                }),
            });
            const data = await response.json();
            if (response.ok) {
                setNewPromptName('');
                setNewPromptContent('');
                setIsAddPromptModalOpen(false);
                handlePromptCreated(menuPromptSubTab, data);
            } else {
                toast.error(data.error || 'Prompt aanmaken mislukt');
            }
        } catch (error) {
            toast.error('Prompt aanmaken mislukt');
        } finally {
            setIsCreatingPrompt(false);
        }
    };

    const fetchSimulations = useCallback(() => {
        console.log('fetchSimulations', simulationsPage, simulationsPageSize, showArchived);
        if (!simulationsPage || !simulationsPageSize) return;
        setSimulationsLoading(true);
        setSimulations([])
        console.log(`${BACKEND_URL}/vnog/simulations/?page=${simulationsPage}&page_size=${simulationsPageSize}&show_archived=${showArchived}`);
        fetch(`${BACKEND_URL}/vnog/simulations/?page=${simulationsPage}&page_size=${simulationsPageSize}&show_archived=${showArchived}`, { credentials: 'include', cache: 'no-store' })
            .then(r => r.json())
            .then(data => {
                console.log('data', data);
                setSimulations(data.results);
                setSimulationsTotal(data.count);
            })
            .catch(() => {})
            .finally(() => setSimulationsLoading(false));
    }, [simulationsPage, simulationsPageSize, showArchived]);

    useEffect(() => {
        fetchSimulations();
    }, [simulationsPage, simulationsPageSize, showArchived]);

    useEffect(() => {
        if (isCreateModalOpen) {
            fetchSimulations();
        }
    }, [isCreateModalOpen]);

    const fetchSimulationDetail = (simulationToken: string) => {
        setSimulationLoading(true);
        return fetch(`${BACKEND_URL}/vnog/simulations/${simulationToken}/`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then((data) => {
                if (data) {
                    setActiveSimulation(data);
                    if (data.characteristics_estimation_prompt) {
                        setCharacteristicsEstimationPrompt(data.characteristics_estimation_prompt);
                    }
                    if (data.ai_operator_prompt) {
                        setAiOperatorPrompt(data.ai_operator_prompt);
                    }
                    if (data.ai_summary_prompt) {
                        setAiSummaryPrompt(data.ai_summary_prompt);
                    }
                    if (data.ai_final_recommendation_filter_prompt) {
                        setAiFinalRecommendationFilterPrompt(data.ai_final_recommendation_filter_prompt);
                    }
                    onSimulationDetailLoaded?.(data);
                }
                return data;
            })
            .catch(() => null)
            .finally(() => setSimulationLoading(false));
    };

    useEffect(() => {
        fetchPrompts();
    }, []);

    useEffect(() => {
        fetch(`${BACKEND_URL}/vnog/is_admin_user/`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => setIsAdminUser(data.is_admin_user === true))
            .catch(() => setIsAdminUser(false));
    }, []);

    // Fetch situations for modal
    useEffect(() => {
        fetch(BACKEND_URL + '/vnog/situations/', {
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
                'Content-Type': 'application/json',
            },
        })
            .then(r => r.json())
            .then(data => setModalSituations(data));
    }, []);

    // Fetch phases and roles for the create modal. Default selections to all once loaded.
    useEffect(() => {
        fetch(BACKEND_URL + '/vnog/phases/', { credentials: 'include' })
            .then(r => r.json())
            .then((data: Phase[]) => {
                setAvailablePhases(data);
                setModalSelectedPhaseIds(data.map(p => p.id));
            })
            .catch(() => {});
        fetch(BACKEND_URL + '/vnog/roles/', { credentials: 'include' })
            .then(r => r.json())
            .then((data: Role[]) => {
                setAvailableRoles(data);
                setModalSelectedRoleIds(data.map(r => r.id));
            })
            .catch(() => {});
    }, []);

    // Fetch active simulation when selectedToken changes
    useEffect(() => {
        if (selectedToken) {
            // Find simulation by token from the simulations list
            const sim = simulations.find(s => s.token === selectedToken);
            if (sim && sim.id !== activeSimulation?.id) {
                setActiveSimulation(sim);
                fetchSimulationDetail(sim.token);
                setIsCreatingNew(false);
            }
        } else {
            setActiveSimulation(null);
        }
    }, [selectedToken, simulations]);

    // Auto-open create modal when no simulation is selected
    useEffect(() => {
        if (!selectedToken && simulations && simulations.length > 0) {
            setIsCreateModalOpen(true);
        }
    }, [selectedToken, simulations]);

    // Fetch situation name for active simulation when needed
    useEffect(() => {
        if (activeSimulation?.situation) {
            // If local selected situation matches the active simulation, use its name
            if (situation && situation.id === activeSimulation.situation && situation.name) {
                setActiveSituationName(situation.name || null);
                return;
            }
            fetch(`${BACKEND_URL}/vnog/situations/${activeSimulation.situation}/`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then((data) => setActiveSituationName(data?.name || null))
                .catch(() => setActiveSituationName(null));
        } else {
            setActiveSituationName(null);
        }
    }, [activeSimulation?.situation, situation?.id]);

    const createSimulation = async () => {
        if (!canCreate) return;
        try {
            setLoading(true);
            const toPause = isCreatingNew ? previousActiveSimulation : activeSimulation;
            if (toPause) {
                await fetch(`${BACKEND_URL}/vnog/simulations/${toPause.token}/pause/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' },
                });
            }
            const body = new URLSearchParams({
                address: JSON.stringify({ zipcode, house_number: huisnummer }),
                situation_id: String(situation!.id),
            });
            if (characteristicsEstimationPrompt?.id) body.append('characteristics_estimation_prompt_id', String(characteristicsEstimationPrompt.id));
            if (aiOperatorPrompt?.id) body.append('ai_operator_prompt_id', String(aiOperatorPrompt.id));
            if (aiSummaryPrompt?.id) body.append('ai_summary_prompt_id', String(aiSummaryPrompt.id));
            if (aiFinalRecommendationFilterPrompt?.id) body.append('ai_final_recommendation_filter_prompt_id', String(aiFinalRecommendationFilterPrompt.id));
            const res = await fetch(`${BACKEND_URL}/vnog/simulations/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-CSRFToken': Cookies.get('csrftoken') || '' },
                body,
            });
            const sim = await res.json();
            setActiveSimulation(sim);
            resetAll();
            if (sim?.token) {
                setSimulationToken(sim.token);
            }
            setIsCreatingNew(false);
            setPreviousActiveSimulation(null);
            fetchSimulations();
        } finally {
            setLoading(false);
        }
    };


    const play = () => {
        if (!activeSimulation) return;
        fetch(`${BACKEND_URL}/vnog/simulations/${activeSimulation.token}/play/`, 
            { method: 'POST', credentials: 'include', headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' } })
            .then(() => {
                setActiveSimulation({ ...activeSimulation, status: 'playing_on_simulation_time' });
                fetchSimulations();
            })
            .catch(() => {
                toast.error('Failed to play simulation');
            });
    };
    const playSequentially = () => {
        if (!activeSimulation) return;
        fetch(`${BACKEND_URL}/vnog/simulations/${activeSimulation.token}/play_sequentially/`, { method: 'POST', credentials: 'include', headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' } })
            .then(() => {
                setActiveSimulation({ ...activeSimulation, status: 'playing_sequentially' });
                fetchSimulations();
            })
            .catch(() => {
                toast.error('Failed to play simulation sequentially');
            });
    };
    const pause = () => {
        if (!activeSimulation) return;
        fetch(`${BACKEND_URL}/vnog/simulations/${activeSimulation.token}/pause/`, { method: 'POST', credentials: 'include', headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' } })
            .then(() => {
                setActiveSimulation({ ...activeSimulation, status: 'paused' });
                fetchSimulations();
            })
            .catch(() => {
                toast.error('Failed to pause simulation');
            });
    };
    const stop = () => {
        if (!activeSimulation) return;
        fetch(`${BACKEND_URL}/vnog/simulations/${activeSimulation.token}/stop/`, 
            { method: 'POST', credentials: 'include', headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' } })
            .then(() => {
                setActiveSimulation({ ...activeSimulation, status: 'finished' });
                fetchSimulations();
            })
            .catch(() => {
                toast.error('Failed to stop simulation');
            });
    };
    const getAIOperatorMessage = () => {
        if (!activeSimulation?.token) return;
        fetch(`${BACKEND_URL}/vnog/get_ai_operator_message/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            body: new URLSearchParams({
                'simulation_token': activeSimulation.token
            }),
        })
        .then(response => response.json())
        .then(data => {
            if (data.error) {
                toast.error(data.error);
            } else {
                toast.success('AI operator message task started');
            }
        })
        .catch(() => {
            toast.error('Failed to get AI operator message');
        });
    };

    // Modal helper functions
    const getSituationName = (situationId: number | null | undefined) => {
        if (!situationId) return 'Free play';
        const match = modalSituations.find(s => s.id === situationId);
        const baseName = match?.name || `Situation ${situationId}`;
        return match?.gms_id ? `${baseName} (GMS ${match.gms_id})` : baseName;
    };

    const getStatusBadge = (status: string | undefined) => {
        const base = 'px-2 py-0.5 rounded text-xs font-medium';
        if (status === 'playing_on_simulation_time' || status === 'playing_sequentially') {
            return <span className={`${base} bg-green-100 text-green-800`}>Running</span>;
        }
        if (status === 'paused') {
            return <span className={`${base} bg-yellow-100 text-yellow-800`}>Paused</span>;
        }
        if (status === 'finished') {
            return <span className={`${base} bg-gray-200 text-gray-700`}>Finished</span>;
        }
        return <span className={`${base} bg-gray-100 text-gray-700`}>{status || 'Unknown'}</span>;
    };

    const createSimulationFromModal = (freePlay: boolean) => {
        // Require address for both modes; situation only for non-free-play
        if (!modalZipcode || !modalHouseNumber) return;
        if (!freePlay && !modalSelectedSituationId) return;
        // Require at least one phase and one role; default is "all selected".
        if (modalSelectedPhaseIds.length === 0 || modalSelectedRoleIds.length === 0) {
            toast.error('Select at least one phase and one role');
            return;
        }
        setModalCreating(true);
        const form = new URLSearchParams();
        form.append('address', JSON.stringify({ zipcode: modalZipcode, house_number: modalHouseNumber }));
        if (!freePlay) form.append('situation_id', modalSelectedSituationId);
        form.append('free_play_simulation', freePlay ? 'true' : 'false');
        if (characteristicsEstimationPrompt?.id) form.append('characteristics_estimation_prompt_id', String(characteristicsEstimationPrompt.id));
        if (aiOperatorPrompt?.id) form.append('ai_operator_prompt_id', String(aiOperatorPrompt.id));
        if (aiSummaryPrompt?.id) form.append('ai_summary_prompt_id', String(aiSummaryPrompt.id));
        form.append('phase_ids', JSON.stringify(modalSelectedPhaseIds));
        form.append('role_ids', JSON.stringify(modalSelectedRoleIds));
        form.append('relevance_threshold', String(modalRelevanceThreshold));
        fetch(BACKEND_URL + '/vnog/simulations/create/', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            body: form,
        })
            .then(res => res.json())
            .then(data => {
                if (data && data.token) {
                    setSimulationToken(data.token);
                    setIsCreateModalOpen(false);
                    fetchSimulations(); // Refresh simulations list
                }
            })
            .finally(() => setModalCreating(false));
    };

    const archiveSimulation = async (token: string) => {
        try {
            const res = await fetch(`${BACKEND_URL}/vnog/simulations/${token}/archive/`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' },
            });
            if (!res.ok) {
                toast.error('Failed to archive simulation');
                return;
            }
            toast.success('Simulatie gearchiveerd');
            fetchSimulations();
        } catch {
            toast.error('Failed to archive simulation');
        }
    };

    const unarchiveSimulation = async (token: string) => {
        try {
            const res = await fetch(`${BACKEND_URL}/vnog/simulations/${token}/unarchive/`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' },
            });
            if (!res.ok) {
                toast.error('Failed to unarchive simulation');
                return;
            }
            toast.success('Simulatie gedearchiveerd');
            fetchSimulations();
        } catch {
            toast.error('Failed to unarchive simulation');
        }
    };

    const simulationPaginationButtons = useCallback(() => {
        const pageCount = Math.ceil(simulationsTotal / simulationsPageSize);
        const buttons = [];
        if (simulationsPage > 1) {
            buttons.push(<button key="previous" onClick={() => setSimulationsPage(simulationsPage - 1)} className="text-sm text-gray-500">{'<'}</button>)
        }

        for (let page = 1; page <= pageCount; page++) {
            buttons.push(<button key={page} onClick={() => setSimulationsPage(page)} className={page === simulationsPage ? "text-sm text-gray-500 font-bold" : "text-sm text-gray-500"}>{page}</button>)
        }

        if (simulationsPage < pageCount) {
            buttons.push(<button key="next" onClick={() => setSimulationsPage(simulationsPage + 1)} className="text-sm text-gray-500">{'>'}</button>)
        }
        return <div className="flex items-center gap-3">{buttons}</div>;
    }, [simulationsTotal, simulationsPage, simulationsPageSize]);

    const PROMPT_TYPES = useMemo(() => [
        { key: 'characteristics_estimation' as const, label: 'Kenmerken', prompt: characteristicsEstimationPrompt },
        { key: 'ai_operator' as const, label: 'AI Operator', prompt: aiOperatorPrompt },
        { key: 'ai_summary' as const, label: 'Samenvatting', prompt: aiSummaryPrompt },
    ], [characteristicsEstimationPrompt, aiOperatorPrompt, aiSummaryPrompt]);

    const setPromptByType = (key: MenuPromptType, prompt: Prompt) => {
        if (key === 'characteristics_estimation') setCharacteristicsEstimationPrompt(prompt);
        else if (key === 'ai_operator') setAiOperatorPrompt(prompt);
        else setAiSummaryPrompt(prompt);
    };

    const modalAddressValue = modalZipcode && modalHouseNumber
        ? `${modalHouseNumber}, ${modalZipcode}`
        : null;
    const modalSituationValue = modalSelectedMode === 'free'
        ? (modalSelectedMode ? 'Free play' : null)
        : modalSelectedSituationId
            ? getSituationName(Number(modalSelectedSituationId))
            : null;

    const openMainMenu = (tab?: 'simulaties' | 'prompts') => {
        if (tab) setMenuTab(tab);
        setIsCreateModalOpen(true);
    };

    const openAddressTile = () => {
        setTileZipcode(modalZipcode);
        setTileHouseNumber(modalHouseNumber);
        setIsAddressTileModalOpen(true);
    };
    const saveAddressTile = () => {
        setModalZipcode(tileZipcode);
        setModalHouseNumber(tileHouseNumber);
        setIsAddressTileModalOpen(false);
    };

    const addressValue = activeSimulation && !isCreatingNew && activeSimulation.address
        ? `${activeSimulation.address.house_number}, ${activeSimulation.address.zipcode}`
        : (modalZipcode.trim() && modalHouseNumber.trim()
            ? `${modalHouseNumber.trim()}, ${modalZipcode.trim()}`
            : (zipcode.trim() && huisnummer.trim() ? `${huisnummer.trim()}, ${zipcode.trim()}` : null));
    const situationValue = activeSimulation && !isCreatingNew
        ? (activeSituationName || (activeSimulation.situation ? getSituationName(activeSimulation.situation) : null))
        : (modalSelectedMode === 'free'
            ? 'Free play'
            : modalSelectedSituationId
                ? getSituationName(Number(modalSelectedSituationId))
                : (situation?.name || null));
    const showLive = activeSimulation && !isCreatingNew && !activeSimulation.free_play_simulation;

    const filteredModalSimulations = useMemo(() => {
        const q = menuSearchQuery.trim().toLowerCase();
        return simulations.filter(sim => {
            if (!showArchived && sim.archived) return false;
            if (!q) return true;
            const haystack = `${sim.token} ${getSituationName(sim.situation)} ${sim.address?.zipcode || ''} ${sim.address?.house_number || ''}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [simulations, menuSearchQuery, showArchived, modalSituations]);

    const canStartFromModal = Boolean(
        modalSelectedMode
        && modalZipcode
        && modalHouseNumber
        && (modalSelectedMode === 'free' || modalSelectedSituationId)
        && modalSelectedPhaseIds.length > 0
        && modalSelectedRoleIds.length > 0
        && !modalCreating
    );

    return (
        <div className="w-full h-full flex flex-col animate-toolFadeIn min-h-0">
            <Title title="Simulatie Manager" />

            <div className="mt-3 flex flex-col md:flex-row gap-6 flex-1 min-h-0">
                <div className="flex-1 min-w-0 flex flex-col gap-4">
                    <div>
                        <SectionHeading>Adres</SectionHeading>
                        <SelectorTile
                            value={addressValue}
                            emptyCta="Adres invoeren"
                            onClick={() => openMainMenu()}
                        />
                    </div>

                    <div>
                        <SectionHeading>Simulatie</SectionHeading>
                        <SelectorTile
                            value={situationValue}
                            emptyCta="Simulatie kiezen"
                            onClick={() => openMainMenu('simulaties')}
                        />
                    </div>

                    <div>
                        <SectionHeading>Prompts</SectionHeading>
                        <div className="flex flex-col gap-2">
                            {PROMPT_TYPES.map(pt => (
                                <SelectorTile
                                    key={pt.key}
                                    subLabel={pt.label}
                                    value={pt.prompt?.name || null}
                                    emptyCta={`${pt.label} kiezen`}
                                    onClick={() => { setMenuPromptSubTab(pt.key); openMainMenu('prompts'); }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-auto">
                        {(!activeSimulation || isCreatingNew) && (
                            <>
                                <Button
                                    onClick={createSimulation}
                                    className={`min-w-[160px] ${(!hasAddress || !situation || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={!hasAddress || !situation || loading}
                                >
                                    {loading ? 'Bezig met aanmaken...' : 'Create Simulation'}
                                </Button>
                                {loading && (
                                    <p className="text-sm text-gray-500 animate-pulse">Adresgegevens worden opgehaald, dit kan even duren...</p>
                                )}
                            </>
                        )}
                        {activeSimulation && !isCreatingNew && activeSimulation.free_play_simulation && (
                            <Button onClick={getAIOperatorMessage} className="min-w-[180px]">Get AI Operator Message</Button>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-3 min-h-0">
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        <Pager pager={pagerSnapshot} simulationElapsed={simulationElapsed} />
                    </div>

                    {showLive && (
                        <div className="grid grid-cols-4 gap-2 w-full flex-shrink-0">
                            <TransportButton label="Afspelen" onClick={play} active={activeSimulation!.status === 'playing_on_simulation_time'}>
                                <IconPlayerPlayFilled size={22} />
                                <span className="text-[10px] mt-0.5 tracking-wider">PLAY</span>
                            </TransportButton>
                            <TransportButton label="Versneld afspelen" onClick={playSequentially} active={activeSimulation!.status === 'playing_sequentially'}>
                                <IconPlayerTrackNextFilled size={22} />
                                <span className="text-[10px] mt-0.5 tracking-wider">FWD</span>
                            </TransportButton>
                            <TransportButton label="Pauze" onClick={pause} active={activeSimulation!.status === 'paused'}>
                                <IconPlayerPauseFilled size={22} />
                                <span className="text-[10px] mt-0.5 tracking-wider">PAUSE</span>
                            </TransportButton>
                            <TransportButton label="Stop" onClick={stop} active={activeSimulation!.status === 'finished'}>
                                <IconPlayerStopFilled size={22} />
                                <span className="text-[10px] mt-0.5 tracking-wider">STOP</span>
                            </TransportButton>
                        </div>
                    )}
                </div>
            </div>

            {isCreateModalOpen && (
                <Modal isOpen={isCreateModalOpen} onRequestClose={() => setIsCreateModalOpen(false)}>
                    <div className="bg-white relative z-100 rounded-xl shadow-2xl p-6 w-[96vw] max-w-[1600px] h-[88vh] overflow-hidden flex flex-col">
                        <div className="flex items-start justify-between mb-4 flex-shrink-0">
                            <h2 className="text-xl font-semibold text-gray-900">Simulaties</h2>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                aria-label="Close"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex gap-6 flex-1 min-h-0">
                            <div className="w-[26%] min-w-[300px] flex flex-col gap-4 overflow-y-auto pr-1">
                                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Nieuwe simulatie</h3>

                                <div>
                                    <SectionHeading>Modus</SectionHeading>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setModalSelectedMode('guided')}
                                            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${modalSelectedMode === 'guided' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                        >
                                            Begeleid
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setModalSelectedMode('free')}
                                            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${modalSelectedMode === 'free' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                        >
                                            Free play
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <SectionHeading>Adres</SectionHeading>
                                    <SelectorTile
                                        value={modalAddressValue}
                                        emptyCta="Adres invoeren"
                                        onClick={openAddressTile}
                                    />
                                </div>

                                {modalSelectedMode === 'guided' && (
                                    <div>
                                        <SectionHeading>Simulatie</SectionHeading>
                                        <SelectorTile
                                            value={modalSituationValue}
                                            emptyCta="Scenario kiezen"
                                            onClick={() => setIsScenarioPickerOpen(true)}
                                        />
                                    </div>
                                )}

                                <div>
                                    <SectionHeading>Documentatie</SectionHeading>
                                    <div className="space-y-3 rounded-lg border border-gray-200 p-3 bg-gray-50">
                                        <TagMultiSelect
                                            label="Toegestane fasen"
                                            options={availablePhases}
                                            selectedIds={modalSelectedPhaseIds}
                                            onChange={setModalSelectedPhaseIds}
                                        />
                                        <TagMultiSelect
                                            label="Toegestane rollen"
                                            options={availableRoles}
                                            selectedIds={modalSelectedRoleIds}
                                            onChange={setModalSelectedRoleIds}
                                        />
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Relevantiedrempel</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.1}
                                                value={modalRelevanceThreshold}
                                                onChange={e => {
                                                    const v = parseFloat(e.target.value);
                                                    setModalRelevanceThreshold(Number.isFinite(v) ? v : 0);
                                                }}
                                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <SectionHeading>Prompts</SectionHeading>
                                    <div className="flex flex-col gap-2">
                                        {PROMPT_TYPES.map(pt => (
                                            <SelectorTile
                                                key={pt.key}
                                                subLabel={pt.label}
                                                value={pt.prompt?.name || null}
                                                emptyCta={`${pt.label} kiezen`}
                                                onClick={() => { setMenuTab('prompts'); setMenuPromptSubTab(pt.key); }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-auto pt-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (modalSelectedMode === 'free') createSimulationFromModal(true);
                                            else createSimulationFromModal(false);
                                        }}
                                        disabled={!canStartFromModal}
                                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-4 py-2.5 rounded-md transition-colors"
                                    >
                                        {modalCreating ? 'Bezig…' : modalSelectedMode === 'free' ? 'Start free play' : 'Start simulatie'}
                                    </button>
                                    {!canStartFromModal && !modalCreating && (
                                        <p className="mt-2 text-xs text-gray-500">
                                            {!modalSelectedMode ? 'Kies een modus.' : !modalZipcode || !modalHouseNumber ? 'Vul postcode en huisnummer in.' : modalSelectedMode === 'guided' && !modalSelectedSituationId ? 'Kies een scenario.' : 'Selecteer minimaal één fase en rol.'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 min-w-0 flex flex-col gap-3 border-l border-gray-200 pl-6">
                                <div className="flex items-center gap-6 border-b border-gray-200">
                                    {([
                                        { key: 'simulaties' as const, label: 'Simulaties' },
                                        { key: 'prompts' as const, label: 'Prompts' },
                                    ]).map(t => (
                                        <button
                                            key={t.key}
                                            type="button"
                                            onClick={() => setMenuTab(t.key)}
                                            className={`-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                                                menuTab === t.key
                                                    ? 'border-blue-600 text-blue-700'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>

                                {menuTab === 'simulaties' && (
                                    <>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <input
                                                type="text"
                                                value={menuSearchQuery}
                                                onChange={e => setMenuSearchQuery(e.target.value)}
                                                placeholder="Zoek op scenario of token…"
                                                className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={!showArchived}
                                                    onChange={e => {
                                                        setSimulationsPage(1);
                                                        setShowArchived(!e.target.checked);
                                                    }}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                Verberg gearchiveerd
                                            </label>
                                            <span className="text-xs text-gray-500 whitespace-nowrap">{simulationsTotal} totaal</span>
                                            <div className="ml-auto flex items-center gap-3">
                                                {isAdminUser && (
                                                    <Button variant="secondary" size="small" onClick={() => navigate('/admin_vnog?tab=simulaties')}>
                                                        Scenario beheren
                                                    </Button>
                                                )}
                                                {simulationsTotal > 0 && simulationPaginationButtons()}
                                            </div>
                                        </div>

                                        <div className="flex-1 overflow-auto border border-gray-200 rounded-md min-h-0">
                                            {simulationsLoading ? (
                                                <div className="p-6 flex justify-center">
                                                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-900" />
                                                </div>
                                            ) : filteredModalSimulations.length === 0 ? (
                                                <div className="p-6 text-center text-sm text-gray-500 italic">
                                                    {simulations.length === 0 ? 'Nog geen simulaties — maak er een links.' : 'Geen simulaties die matchen.'}
                                                </div>
                                            ) : (
                                                <table className="w-full text-sm">
                                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                                        <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                                            <th className="px-4 py-3">Naam</th>
                                                            <th className="px-4 py-3 whitespace-nowrap">Adres</th>
                                                            <th className="px-4 py-3">Status</th>
                                                            <th className="px-4 py-3 whitespace-nowrap">Aangemaakt</th>
                                                            <th className="px-4 py-3"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {filteredModalSimulations.map(sim => {
                                                            const isSelected = sim.token === selectedToken;
                                                            return (
                                                                <tr
                                                                    key={sim.token}
                                                                    className={`transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} ${sim.archived ? 'opacity-50' : ''}`}
                                                                >
                                                                    <td className="px-4 py-3 align-top">
                                                                        <div className="font-medium text-gray-900">{getSituationName(sim.situation)}</div>
                                                                        <div className="text-[10px] text-gray-400 font-mono truncate max-w-[220px]">{sim.token}</div>
                                                                    </td>
                                                                    <td className="px-4 py-3 align-top text-gray-700 whitespace-nowrap">
                                                                        {sim.address ? `${sim.address.house_number}, ${sim.address.zipcode}` : <span className="text-gray-400">—</span>}
                                                                    </td>
                                                                    <td className="px-4 py-3 align-top">{getStatusBadge(sim.status)}</td>
                                                                    <td className="px-4 py-3 align-top text-gray-700 whitespace-nowrap text-xs">
                                                                        {new Date(sim.created_at).toLocaleString('nl-NL', {
                                                                            day: 'numeric', month: 'short', year: 'numeric',
                                                                            hour: '2-digit', minute: '2-digit',
                                                                        })}
                                                                    </td>
                                                                    <td className="px-4 py-3 align-top whitespace-nowrap">
                                                                        <div className="flex flex-col items-stretch gap-2 w-28 ml-auto">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    if (isSelected) return;
                                                                                    setSimulationToken(sim.token);
                                                                                    setIsCreateModalOpen(false);
                                                                                }}
                                                                                disabled={isSelected}
                                                                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                                                                    isSelected
                                                                                        ? 'bg-blue-100 text-blue-800 border border-blue-300 cursor-default'
                                                                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                                                                }`}
                                                                            >
                                                                                {isSelected ? 'Geselecteerd' : 'Selecteer'}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => sim.archived ? unarchiveSimulation(sim.token) : archiveSimulation(sim.token)}
                                                                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${sim.archived ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                                                                            >
                                                                                {sim.archived ? 'Dearchiveer' : 'Archiveer'}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </>
                                )}

                                {menuTab === 'prompts' && (
                                    <div className="flex-1 min-h-0 flex flex-col gap-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                {PROMPT_TYPES.map(pt => (
                                                    <button
                                                        key={pt.key}
                                                        type="button"
                                                        onClick={() => setMenuPromptSubTab(pt.key)}
                                                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                                                            menuPromptSubTab === pt.key
                                                                ? 'bg-blue-600 text-white border-blue-600'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {pt.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={() => {
                                                    setNewPromptName('');
                                                    setNewPromptContent('');
                                                    setIsAddPromptModalOpen(true);
                                                }}
                                            >
                                                Prompt toevoegen
                                            </Button>
                                        </div>

                                        <div className="flex-1 min-h-0 flex gap-4">
                                            {(() => {
                                                const typeMeta = PROMPT_TYPES.find(pt => pt.key === menuPromptSubTab);
                                                const selectedId = typeMeta?.prompt?.id;
                                                const versions = prompts.filter(p => p.prompt_type === menuPromptSubTab);
                                                const shown = (previewPrompt && previewPrompt.prompt_type === menuPromptSubTab) ? previewPrompt : typeMeta?.prompt;
                                                return (
                                                    <>
                                                        <div className="w-[40%] min-w-[280px] overflow-auto border border-gray-200 rounded-md p-3">
                                                            {versions.length === 0 ? (
                                                                <div className="p-6 text-center text-sm text-gray-500 italic">Geen versies beschikbaar.</div>
                                                            ) : (
                                                                <div className="flex flex-col gap-2">
                                                                    {versions.map(v => {
                                                                        const isSel = v.id === selectedId;
                                                                        return (
                                                                            <div
                                                                                key={v.id}
                                                                                onClick={() => setPreviewPrompt(v)}
                                                                                className={`rounded-lg border p-3 cursor-pointer transition-all ${isSel ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/40' : 'border-gray-300 hover:border-gray-400'}`}
                                                                            >
                                                                                <div className="flex items-start justify-between gap-2">
                                                                                    <span className="text-sm font-medium text-gray-900 truncate">{v.name}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={e => { e.stopPropagation(); setPromptByType(menuPromptSubTab, v); }}
                                                                                        disabled={isSel}
                                                                                        className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium ${isSel ? 'bg-blue-100 text-blue-800' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                                                                    >
                                                                                        {isSel ? '✓' : 'Kies'}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0 overflow-auto border border-gray-200 rounded-md p-4 bg-gray-50">
                                                            {!shown ? (
                                                                <div className="h-full flex items-center justify-center text-sm text-gray-500 italic">Geen prompt geselecteerd.</div>
                                                            ) : (
                                                                <div className="space-y-2">
                                                                    <h2 className="text-lg font-semibold text-gray-900">{shown.name}</h2>
                                                                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono bg-white rounded-lg border border-gray-200 p-3">{shown.prompt || '(geen inhoud)'}</pre>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {isAddressTileModalOpen && (
                <Modal isOpen={isAddressTileModalOpen} onRequestClose={() => setIsAddressTileModalOpen(false)}>
                    <div className="bg-white relative z-100 rounded-xl shadow-2xl p-6 w-[90vw] max-w-md">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Adres invoeren</h2>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Zoek adres</label>
                                <PDOKAddressSearch
                                    onSelect={(addr) => {
                                        setTileZipcode(addr.zipcode);
                                        setTileHouseNumber(addr.houseNumber);
                                    }}
                                />
                                <p className="mt-1 text-xs text-gray-500">of vul postcode en huisnummer handmatig in</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                                <input
                                    type="text"
                                    value={tileZipcode}
                                    onChange={e => setTileZipcode(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Huisnummer</label>
                                <input
                                    type="text"
                                    value={tileHouseNumber}
                                    onChange={e => setTileHouseNumber(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setIsAddressTileModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Annuleren</button>
                            <button onClick={saveAddressTile} className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700">Opslaan</button>
                        </div>
                    </div>
                </Modal>
            )}

            {isAddPromptModalOpen && (
                <Modal isOpen={isAddPromptModalOpen} onRequestClose={() => setIsAddPromptModalOpen(false)}>
                    <div className="bg-white relative z-100 rounded-xl shadow-2xl p-6 w-[90vw] max-w-3xl max-h-[85vh] flex flex-col">
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">Prompt toevoegen</h2>
                            <button
                                type="button"
                                onClick={() => setIsAddPromptModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                aria-label="Sluiten"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4 flex-1 min-h-0 flex flex-col">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                                    {PROMPT_TYPES.find(pt => pt.key === menuPromptSubTab)?.label || menuPromptSubTab}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Naam *</label>
                                <input
                                    type="text"
                                    value={newPromptName}
                                    onChange={e => setNewPromptName(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Naam van de prompt"
                                    autoFocus
                                />
                            </div>
                            <div className="flex-1 min-h-0 flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
                                <textarea
                                    value={newPromptContent}
                                    onChange={e => setNewPromptContent(e.target.value)}
                                    className="w-full flex-1 min-h-[300px] rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                    placeholder="Voer de prompt hier in…"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                type="button"
                                onClick={() => setIsAddPromptModalOpen(false)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Annuleren
                            </button>
                            <button
                                type="button"
                                onClick={createNewPrompt}
                                disabled={!newPromptName.trim() || isCreatingPrompt}
                                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isCreatingPrompt ? 'Bezig…' : 'Opslaan'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {isScenarioPickerOpen && (
                <Modal isOpen={isScenarioPickerOpen} onRequestClose={() => setIsScenarioPickerOpen(false)}>
                    <div className="bg-white relative z-100 rounded-xl shadow-2xl p-6 w-[90vw] max-w-lg max-h-[80vh] flex flex-col">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Scenario kiezen</h2>
                        <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                            {modalSituations.length === 0 ? (
                                <div className="p-4 text-sm text-gray-500 italic">Geen scenario&apos;s beschikbaar.</div>
                            ) : modalSituations.map(s => {
                                const isSelected = modalSelectedSituationId === String(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => {
                                            setModalSelectedSituationId(String(s.id));
                                            setModalSelectedMode('guided');
                                            setIsScenarioPickerOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${isSelected ? 'bg-blue-50 text-blue-900 font-medium' : 'hover:bg-gray-50 text-gray-800'}`}
                                    >
                                        {s.name || `Scenario ${s.id}`}{s.gms_id ? ` (GMS ${s.gms_id})` : ''}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex justify-end mt-4">
                            <button onClick={() => setIsScenarioPickerOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Sluiten</button>
                        </div>
                    </div>
                </Modal>
            )}

        </div>
    )
}

export default SimulationManager;